import { Coins, FileText, Layers, Pencil, Printer, Trash2, Wallet } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, type BadgeProps, Card, Skeleton } from '../../components/shadcn/index.js';
import {
  AnimatedNumber,
  Button,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  StatCard,
  type StatTone,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { appUrl } from '../../lib/basePath.js';
import { formatDate, formatNumber } from '../../lib/intl.js';
import { moduleEnabled, type ModuleKey } from '../../lib/modules.js';
import { useEnabledModules } from '../../lib/modulesStore.js';
import { formatMoney } from '../../lib/money.js';
import { fetchAllParties } from '../../lib/parties.js';

import { userNote, useShowNotes } from './docNotes.js';
import { partyPath } from './partyPath.js';
import { isVoidDoc, localToday, type PayState, payStateOf, remainingOf } from './paymentState.js';
import { reviseErrorMessage } from './reviseErrors.js';

interface DocumentListPageProps {
  /** Translation namespace key (e.g. "salesOrders", "invoices"). */
  ns: string;
  /** API path under /api/v1, e.g. "/sales-orders". */
  endpoint: string;
  /** Date column on the row (camelCased). 'docDate' for most; 'paymentDate' for payments. */
  dateField?: string;
  /** Tone map for status values — the old neutral/brand kit tones, mapped onto shadcn Badge variants. */
  statusTone?: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'brand'>;
  /** If set, renders a "+ New" link to this path; gated on `writePermission`. */
  newPath?: string;
  /**
   * If set, the "+ New" link ALSO needs this toggleable module to be on. The
   * affordance only — the list, the row actions and Edit are untouched, because
   * a tenant that stops manual entry still has to fix the documents it has.
   */
  newModule?: ModuleKey;
  /** Permission for the "+ New" link. */
  writePermission?: string;
  /** If set, each row gets a Post action that POSTs to `${endpoint}/:id/post`. */
  postPermission?: string;
  /** If set, doc number cell links to `${detailPath}/${row.id}`. */
  detailPath?: string;
  /** If set, each row gets a Print icon linking to `${printPath}/${row.id}/print`. */
  printPath?: string;
  /** If set, draft rows get an Edit link to `${detailPath}/${row.id}/edit`. */
  editable?: boolean;
  /** Query params for the LIST request (e.g. `{ origin: 'field' }`). */
  listParams?: Record<string, string>;
  /**
   * Sales orders only: show the not-invoiced / invoiced / all switch, and hide
   * invoiced orders by default.
   *
   * Opt-IN rather than on for every list, because the same endpoint also feeds
   * the field-rep order page, where a rep's billed orders vanishing would be a
   * surprise nobody asked for.
   */
  showBilledFilter?: boolean;
  /**
   * Heading override, as keys in the COMMON namespace. A filtered view of a document
   * type reuses every `columns.*` / `status.*` string of its parent — only the title
   * and the sentence under it differ — so this beats cloning the whole sub-tree.
   */
  titleKey?: string;
  descriptionKey?: string;
  /** Show which field rep took the order. Sales orders only — nothing else has one. */
  showSalesRep?: boolean;
}

interface DocRow {
  id: string;
  docNumber: string;
  docDate?: string;
  paymentDate?: string;
  partyId?: string;
  /** Resolved server-side — see partyOf(). */
  partyName?: string | null;
  partyCode?: string | null;
  status: string;
  currencyCode: string;
  total?: string;
  amount?: string;
  /** Invoices + bills only — how much of the total has been settled so far. */
  amountPaid?: string;
  /** Invoices + bills only — when the balance falls due (drives "overdue"). */
  dueDate?: string | null;
  /** Invoices/bills: a refund document. Money owed BACK, so it is not "unpaid". */
  isCreditNote?: number | boolean;
  isDebitNote?: number | boolean;
  /** Sales orders only — the server resolves the rep id to a name on the list. */
  salesRepName?: string | null;
  /**
   * Who raised the document — resolved server-side, page-bounded. Null is a
   * real answer: a storefront order is placed by a customer, so there is no
   * staff author to name.
   */
  createdByName?: string | null;
  /** Invoices only — where it was sold (resolved server-side, page-bounded). */
  source?: SaleSource;
  /** Purchase docs only — where posting receives the goods. Null = tenant default. */
  warehouseId?: string | null;
  /** Payments only — 'outbound' means the party is a supplier, not a customer. */
  direction?: string | null;
  /** Payments only — how the money moved (cash / transfer / card …). */
  method?: string | null;
  /**
   * Payments only, resolved server-side. `allocatedAmount` is how much of the
   * payment was pinned to documents (the rest sits on account) and `settles`
   * names the first few of them; `partyBalance` is what the party still owes
   * across their whole account, per currency — the figure the counter is asked
   * for every time somebody pays something off.
   */
  allocatedAmount?: string;
  allocationCount?: number;
  settles?: string[];
  partyBalance?: Array<{ currencyCode: string; amount: string }>;
  /**
   * What is on the document: distinct PRODUCTS and total units, resolved
   * server-side. Undefined for a document type with no lines (payments), which
   * is what hides the column rather than a per-page flag.
   */
  lineCount?: number;
  unitCount?: number;
  postedJournalEntryId?: string | null;
}

/**
 * Minimal party shape the lookup needs. The full Party schema in
 * shared has billingAddress + AR/AP account references that this
 * list page doesn't care about; declaring a narrow interface keeps
 * the type-safety without dragging the wider type in.
 */
interface PartyLite {
  id: string;
  code: string;
  displayName: string;
}

const TONE_TO_VARIANT: Record<string, BadgeProps['variant']> = {
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  info: 'info',
  neutral: 'secondary',
  brand: 'default',
};

/**
 * The rows from the last visit to each list, kept for the life of the tab.
 *
 * Every navigation back to a list used to blank the page to a skeleton and wait
 * out a full round trip — ~450 ms of it pure latency on the shop's link, before
 * the server has done anything. The cached page paints immediately and the
 * fresh one replaces it when it lands, so moving between an invoice and the
 * list stops feeling like a page load. It is never the source of truth: a
 * refresh always follows, and every write path already reloads.
 */
const lastRows = new Map<string, DocRow[]>();
/** One request's worth of rows — the endpoint's own maximum. */
const PAGE = 500;
/**
 * The most a list loads with NO date range: the newest this many. Past it the
 * page says so and points at the date filter, which goes to the server and so
 * reaches any age without dragging a year of documents into the browser.
 */
const MAX_ROWS = 5000;

const PAY_STATE_TONE: Record<Exclude<PayState, 'none'>, BadgeProps['variant']> = {
  paid: 'success',
  partial: 'warning',
  unpaid: 'secondary',
  overdue: 'danger',
};
const numberOf = (v: unknown): number => Number(v ?? 0) || 0;
/**
 * What the party on this payment still owes, in the payment's OWN currency.
 *
 * A customer can be behind in two currencies at once; summing them would be
 * meaningless, so the column answers for the currency the money just moved in
 * and names the rest in its tooltip.
 */
const balanceOf = (row: DocRow): number =>
  numberOf(row.partyBalance?.find((b) => b.currencyCode === row.currencyCode)?.amount);

/** Where an invoice was sold: the cashier (till), the web register, the online store, a rep, the office. */
type SaleSource = 'till' | 'register' | 'online' | 'field' | 'office';
const SALE_SOURCES: SaleSource[] = ['till', 'register', 'online', 'field', 'office'];
const SOURCE_TONE: Record<SaleSource, BadgeProps['variant']> = {
  till: 'info', register: 'warning', online: 'success', field: 'default', office: 'secondary',
};

export function DocumentListPage({
  ns,
  endpoint,
  dateField = 'docDate',
  statusTone,
  newPath,
  newModule,
  writePermission,
  postPermission,
  detailPath,
  printPath,
  editable,
  listParams,
  showBilledFilter = false,
  titleKey,
  descriptionKey,
  showSalesRep,
}: DocumentListPageProps): JSX.Element {
  // `ns` arrives as e.g. "documents:invoices" from the router. i18next does
  // NOT auto-split that string into (namespace, keyPrefix) — the colon is
  // only meaningful inside `t(...)` calls. Split it ourselves so the
  // existing call sites (`t('title')`) keep resolving against the
  // intended documents.<section>.* sub-tree.
  const [nsName, keyPrefix] = ns.includes(':') ? ns.split(':', 2) : [ns, undefined];
  const { t, i18n } = useTranslation(nsName ?? ns, keyPrefix ? { keyPrefix } : undefined);
  const { t: tc } = useTranslation();
  const canWrite = usePermission(writePermission ?? '');
  const canPost = usePermission(postPermission ?? '');
  /** Removing a sales order past draft voids its invoice — the invoice-posting right (as the server). */
  const canUnwindOrders = usePermission('invoices.post');
  /** The documents namespace at its root — the order-removal sentences live there, not under this list's prefix. */
  const { t: tDocs } = useTranslation('documents');
  const canReadParties = usePermission('parties.read');
  // The "+ New" affordance, resolved once: a path, the permission, and — when the
  // caller named one — the module. Read through the store rather than a prop so
  // the button appears/disappears the moment the toggle is saved.
  const tenantId = useAuthStore((s) => s.tenant?.id);
  const enabledModules = useEnabledModules(tenantId);
  const canCreate = !!newPath && canWrite && (!newModule || moduleEnabled(enabledModules, newModule));
  /**
   * Sales orders only: hide the ones already turned into invoices.
   *
   * Defaults to 'open' because this page is a WORKLIST — an order that has been
   * billed is finished, and its invoice is the live document. It appearing here
   * too made the same sale look like two pieces of work. 'invoiced' and 'all'
   * are one click away, so nothing becomes unreachable.
   *
   * Declared before `items` because it is part of the cache key the rows are
   * seeded from.
   */
  const [billed, setBilled] = useState<'open' | 'invoiced' | 'all'>('open');
  // The date range rides the REQUEST (see the server's from/to), so it is part of
  // the key: a range reaches documents of any age, not just the loaded ones.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // Serialised, because a fresh object literal from the parent's render would make an
  // object-identity dep re-fetch on every render.
  const params = {
    ...(listParams ?? {}),
    ...(showBilledFilter ? { billed } : {}),
    ...(fromDate ? { from: fromDate } : {}),
    ...(toDate ? { to: toDate } : {}),
  };
  const paramsKey = JSON.stringify(params);
  const cacheKey = `${endpoint}?${paramsKey}`;
  // Seeded from the last visit to THIS list (see lastRows) so a revisit has rows
  // on screen before the request is even sent; null only on the very first one.
  const [items, setItems] = useState<DocRow[] | null>(() => lastRows.get(cacheKey) ?? null);
  /** Rows the server has for these filters, and whether more are still coming. */
  const [loadState, setLoadState] = useState<{ have: number; total: number; loading: boolean }>({ have: 0, total: 0, loading: false });
  /** Bumped per load, so a slow page from an earlier filter can't land on a newer one. */
  const loadSeq = useRef(0);
  const [statusFilter, setStatusFilter] = useState('all');
  // Where the money stands — independent of the workflow status above, and the
  // filter the shop actually reaches for ("who still owes me?").
  const [payFilter, setPayFilter] = useState<'all' | Exclude<PayState, 'none'>>('all');
  // Branch filter — client-side over the fetched page like status/date; the
  // rows already carry warehouseId for the warehouse-bearing doc types.
  const [warehouseFilter, setWarehouseFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState<'all' | SaleSource>('all');
  // Payments only: money in vs money out. One list holds customer receipts and
  // supplier payments together, and "what did we take today" is a different
  // question from "what did we pay out" — client-side, like every filter here.
  const [directionFilter, setDirectionFilter] = useState<'all' | 'inbound' | 'outbound'>('all');
  // A Notes column, per list, for whoever wants it — remembered in this browser.
  const [showNotes, setShowNotes] = useShowNotes(endpoint);
  // Party directory for the row-level lookup. Loaded once on mount;
  // failure is non-fatal (cell falls back to "—" for unknown partyIds).
  const [parties, setParties] = useState<PartyLite[]>([]);
  // Documents that name a warehouse (bills/POs receive, invoices/SOs ship) show it by name.
  const showWarehouse = endpoint === '/bills' || endpoint === '/purchase-orders' || endpoint === '/invoices' || endpoint === '/sales-orders';
  const [warehouseNames, setWarehouseNames] = useState<Map<string, string>>(new Map());

  /**
   * EVERY page, not the first one. The endpoint answers 200 rows by default and
   * this used to stop there — on Duhok, two days of invoices — while the search,
   * the status/date filters and the totals strip all ran over those 200 as if
   * they were the shop. Now it pages through (500 at a time) up to MAX_ROWS, or
   * everything in the chosen date range. A first visit fills in page by page; a
   * revisit keeps the last full list on screen until the new one is complete.
   */
  const reload = async (): Promise<void> => {
    const seq = ++loadSeq.current;
    const cap = fromDate || toDate ? Number.POSITIVE_INFINITY : MAX_ROWS;
    const progressive = !lastRows.has(cacheKey);
    const rows: DocRow[] = [];
    setLoadState((s) => ({ ...s, have: 0, loading: true }));
    try {
      for (let offset = 0; ; offset += PAGE) {
        // Params ride the LIST call only. Every other request this page makes is
        // `${endpoint}/${id}` for one document, where a filter would be meaningless.
        const r = await api().get<{ items: DocRow[]; total?: number }>(endpoint, {
          params: { ...params, limit: PAGE, offset },
        });
        if (seq !== loadSeq.current) return; // the filters changed under us
        rows.push(...r.data.items);
        const total = r.data.total ?? rows.length;
        const done = r.data.items.length < PAGE || rows.length >= total || rows.length >= cap;
        if (progressive || done) setItems([...rows]);
        setLoadState({ have: rows.length, total, loading: !done });
        if (done) {
          lastRows.set(cacheKey, rows);
          return;
        }
      }
    } catch (e) {
      if (seq === loadSeq.current) setLoadState((s) => ({ ...s, loading: false }));
      throw e;
    }
  };
  useEffect(() => {
    // Paint the last known page first — then refresh it in place. A revisit
    // shows the table immediately instead of a skeleton and a round trip.
    const cached = lastRows.get(cacheKey);
    if (cached) setItems(cached);
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, paramsKey]);

  /**
   * The party directory is a FALLBACK ONLY, and it is fetched only when the
   * rows actually need it.
   *
   * The server resolves `partyName`/`partyCode` per row (and has to — this
   * endpoint returns one page of parties, so on a shop with 4,483 of them the
   * browser-side map was missing most customers anyway). Fetching it
   * unconditionally cost every document list a second request and ~130 KB of
   * JSON that nothing read. It now loads only if a row arrives with a partyId
   * the server did not name — i.e. a browser talking to an older API.
   */
  const needsPartyLookup = !!items?.some((r) => r.partyId && !r.partyName);
  useEffect(() => {
    if (!needsPartyLookup) return;
    void (async () => {
      try {
        setParties(await fetchAllParties<PartyLite>());
      } catch {
        // Non-fatal — rows fall back to "—" for unresolved partyIds.
      }
    })();
  }, [needsPartyLookup]);

  useEffect(() => {
    if (!showWarehouse) return;
    void api()
      .get<{ items: Array<{ id: string; name: string }> }>('/warehouses')
      .then((r) => setWarehouseNames(new Map(r.data.items.map((w) => [w.id, w.name]))))
      .catch(() => { /* non-fatal — the column falls back to "—" */ });
  }, [showWarehouse]);

  const partyById = useMemo(() => {
    const m = new Map<string, PartyLite>();
    for (const p of parties) m.set(p.id, p);
    return m;
  }, [parties]);

  if (!items) {
    return (
      <Card className="flex flex-col gap-3 p-6">
        <Skeleton className="h-7 w-48" />
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center justify-between gap-4">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-4 w-24" />
          </div>
        ))}
      </Card>
    );
  }

  const onPost = async (id: string): Promise<void> => {
    if (!(await confirmDialog({ message: t('actions.confirmPost', { defaultValue: 'Post this document?' }) }))) return;
    try {
      await api().post(`${endpoint}/${id}/post`, {});
      await reload();
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      toast.error(code === 'unprocessable' ? tc('errors:unprocessable') : tc('errors:server'));
    }
  };

  /**
   * Remove a row. A draft is simply deleted. A SALES ORDER past draft — an app
   * order waiting, a till or register sale — comes apart with everything it
   * caused, exactly as from the order page (owner, 2026-11-01: "a remove button
   * on أوامر البيع"): the invoice it became is voided, the payment on it
   * reversed, the stock put back. An order never billed has nothing to reverse,
   * and the confirmation says which of the two this is.
   */
  const onRemove = async (row: DocRow): Promise<void> => {
    const cascading = endpoint === '/sales-orders' && row.status !== 'draft';
    // Whether it became an invoice: the billed filter says so outright (the
    // server lists "not yet invoiced" by the invoice link itself — a «منجز» app
    // order can be in it); only the unfiltered list falls back to the status.
    const billedRow = showBilledFilter && billed !== 'all' ? billed === 'invoiced' : row.status === 'fulfilled';
    const confirmed = await confirmDialog({
      message: !cascading
        ? t('actions.confirmDelete', { defaultValue: 'Delete this draft? This cannot be undone.' })
        : billedRow
          ? tDocs('revise.cancelOrder', {
              doc: row.docNumber,
              defaultValue: 'This removes {{doc}} and undoes everything it caused: the invoice it became is voided, any payment against it is reversed, and stock it took is put back. The journal entries are reversed, never deleted — the invoice stays visible, marked cancelled.',
            })
          : tDocs('revise.removeOpenOrder', {
              doc: row.docNumber,
              defaultValue: 'Remove order {{doc}}? It was never turned into an invoice, so no money or stock moves. This cannot be undone.',
            }),
      tone: 'danger',
      ...(cascading && billedRow
        ? {
            title: tDocs('revise.cancelOrderTitle', { defaultValue: 'Remove this order and reverse its money?' }),
            confirmLabel: tDocs('revise.cancelOrderConfirm', { defaultValue: 'Remove and reverse' }),
          }
        : {}),
    });
    if (!confirmed) return;
    try {
      await api().delete(`${endpoint}/${row.id}${cascading ? '?cascade=1' : ''}`);
      await reload();
    } catch (e) {
      if (cascading) {
        // The server names what is in the way (a credit note, a closed period…).
        toast.error(reviseErrorMessage(e, tDocs, tc));
        return;
      }
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      toast.error(code === 'unprocessable' ? tc('errors:unprocessable') : tc('errors:server'));
    }
  };

  // Row eligibility (shared by the per-row actions and the bulk bar).
  const isPostable = (row: DocRow): boolean =>
    !!postPermission && canPost && !row.postedJournalEntryId &&
    (row.status === 'draft' || row.status === 'issued' || row.status === 'approved');
  const isDeletable = (row: DocRow): boolean => canWrite && row.status === 'draft';

  // Bulk actions — confirm once, then apply per row (failures are tolerated so
  // one bad row doesn't abort the batch), then reload + clear the selection.
  const bulkPost = async (rows: DocRow[], clear: () => void): Promise<void> => {
    const targets = rows.filter(isPostable);
    if (targets.length === 0) return;
    if (!(await confirmDialog({ message: t('actions.confirmPostBulk', { defaultValue: 'Post {{count}} document(s)?', count: targets.length }) }))) return;
    let failed = 0;
    for (const r of targets) {
      try { await api().post(`${endpoint}/${r.id}/post`, {}); } catch { failed += 1; }
    }
    await reload();
    clear();
    if (failed > 0) toast.error(tc('errors:server'));
  };
  const bulkDelete = async (rows: DocRow[], clear: () => void): Promise<void> => {
    const targets = rows.filter(isDeletable);
    if (targets.length === 0) return;
    if (!(await confirmDialog({
      message: t('actions.confirmDeleteBulk', { defaultValue: 'Delete {{count}} draft(s)? This cannot be undone.', count: targets.length }),
      tone: 'danger',
    }))) return;
    let failed = 0;
    for (const r of targets) {
      try { await api().delete(`${endpoint}/${r.id}`); } catch { failed += 1; }
    }
    await reload();
    clear();
    if (failed > 0) toast.error(tc('errors:server'));
  };
  const canBulk = canWrite || (!!postPermission && canPost);

  const header = (
    <header className="flex items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{titleKey ? tc(titleKey) : t('title')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{descriptionKey ? tc(descriptionKey) : t('description')}</p>
      </div>
      {canCreate && newPath && (
        <Link to={newPath}>
          <Button>{tc('actions.create')}</Button>
        </Link>
      )}
    </header>
  );

  if (items.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <EmptyState
          icon={<FileText className="h-8 w-8" aria-hidden />}
          title={t('empty')}
          description={tc('states.documentsEmpty')}
          action={
            canCreate && newPath ? (
              <Link to={newPath}>
                <Button size="sm">{tc('actions.create')}</Button>
              </Link>
            ) : undefined
          }
        />
      </div>
    );
  }

  const dateOf = (row: DocRow): string | undefined =>
    (row as unknown as Record<string, unknown>)[dateField] as string | undefined;
  /**
   * The customer for a row.
   *
   * The server now sends `partyName`/`partyCode` on each row, which is the only
   * version that works: the local map is built from GET /parties, and that
   * endpoint returns one page (200 by default, 500 at most). On a shop with more
   * customers than that, every order whose customer fell outside the page showed
   * "—" while its neighbours looked fine.
   *
   * The map is kept purely as a fallback, so a browser that is momentarily
   * running against an older API still resolves what it can.
   */
  const partyOf = (row: DocRow): PartyLite | undefined => {
    if (row.partyName) return { id: row.partyId ?? '', code: row.partyCode ?? '', displayName: row.partyName };
    return row.partyId ? partyById.get(row.partyId) : undefined;
  };

  // Date-range + status filters (the table itself handles search/sort/paginate).
  const statuses = Array.from(new Set(items.map((r) => r.status)));
  // Presence-driven, like the items column: only invoices and bills carry a paid
  // figure, so only they grow the money columns — no per-page flag to keep in sync.
  const showPayment = items.some((r) => r.amountPaid !== undefined);
  // Payments, same presence rule: only they carry a direction and an allocation
  // figure, so only they grow the settlement + balance columns.
  const isPayments = items.some((r) => r.direction != null);
  const showSettlement = items.some((r) => r.allocatedAmount !== undefined);
  // Invoices: the server says where each was sold.
  const showSource = items.some((r) => r.source !== undefined);
  const sourceLabel = (s: SaleSource): string => tc(`documents:columnsShared.source_${s}`, {
    defaultValue: { till: 'Cashier', register: 'Register', online: 'Online store', field: 'Rep', office: 'Office' }[s],
  });
  const today = localToday();
  const anyFilter = fromDate !== '' || toDate !== '' || statusFilter !== 'all'
    || warehouseFilter !== 'all' || payFilter !== 'all' || directionFilter !== 'all' || sourceFilter !== 'all';
  const filteredItems = items.filter((row) => {
    if (statusFilter !== 'all' && row.status !== statusFilter) return false;
    if (payFilter !== 'all' && payStateOf(row, today) !== payFilter) return false;
    if (sourceFilter !== 'all' && row.source !== sourceFilter) return false;
    if (directionFilter !== 'all' && (row.direction ?? 'inbound') !== directionFilter) return false;
    if (warehouseFilter !== 'all') {
      if (warehouseFilter === 'none' ? row.warehouseId != null : row.warehouseId !== warehouseFilter) return false;
    }
    const d = (dateOf(row) ?? '').slice(0, 10);
    if (fromDate && (!d || d < fromDate)) return false;
    if (toDate && (!d || d > toDate)) return false;
    return true;
  });

  // At-a-glance totals over the *filtered* set, so the strip reacts live as
  // the date/status filters change. Amounts are grouped by currency (summing
  // across currencies would be meaningless); "paid/outstanding" tiles only
  // appear for document types that carry an amountPaid (e.g. invoices, bills).
  // A cancelled / voided document is in NO total: its entry was reversed, so it
  // is neither value nor owed. Each status tile carries its own amount instead,
  // cancelled included — counted on its own, never mixed into the rest.
  const intFmt = (n: number): string => String(Math.round(n));
  const moneyFmt = (cur: string) => (n: number): string =>
    formatMoney(n.toFixed(2), { locale: i18n.language, currency: cur });
  const summary = (() => {
    const byCurrency = new Map<string, { total: number; paid: number; in: number; out: number }>();
    const byStatus = new Map<string, number>();
    /** status → currency → amount, for the status tiles. */
    const statusAmounts = new Map<string, Map<string, number>>();
    let hasPaid = false;
    let overdue = 0;
    for (const row of filteredItems) {
      const amt = Number(row.total ?? row.amount ?? '0') || 0;
      byStatus.set(row.status, (byStatus.get(row.status) ?? 0) + 1);
      const sa = statusAmounts.get(row.status) ?? new Map<string, number>();
      sa.set(row.currencyCode, (sa.get(row.currencyCode) ?? 0) + amt);
      statusAmounts.set(row.status, sa);
      if (isVoidDoc(row)) continue;
      const acc = byCurrency.get(row.currencyCode) ?? { total: 0, paid: 0, in: 0, out: 0 };
      acc.total += amt;
      // Payments: a single "value" tile adds receipts to supplier payments and
      // calls the result a number. Split by direction so each tile means one thing.
      if (row.direction === 'outbound') acc.out += amt;
      else if (row.direction) acc.in += amt;
      if (row.amountPaid != null) {
        hasPaid = true;
        acc.paid += Number(row.amountPaid) || 0;
        if (payStateOf(row, today) === 'overdue') overdue += 1;
      }
      byCurrency.set(row.currencyCode, acc);
    }
    return {
      byCurrency: [...byCurrency.entries()],
      byStatus: [...byStatus.entries()],
      statusAmounts,
      hasPaid,
      overdue,
    };
  })();

  const summaryStrip = (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
      <StatCard
        label={tc('summary.total', { defaultValue: 'Total' })}
        value={<AnimatedNumber value={filteredItems.length} format={intFmt} />}
        icon={Layers}
        tone="brand"
        hint={tc('summary.documents', { defaultValue: 'documents' })}
      />
      {summary.byCurrency.slice(0, 3).map(([cur, sums]) => (
        isPayments ? (
          <Fragment key={`pay-${cur}`}>
            <StatCard
              label={`${tc('documents:payments.filters.direction_inbound', { defaultValue: 'Received' })} · ${cur}`}
              value={<AnimatedNumber value={sums.in} format={moneyFmt(cur)} />}
              icon={Coins}
              tone="success"
            />
            {/* Only where there IS money going out — a shop that never pays a
                supplier in this currency gets a tile of zeros otherwise. */}
            {sums.out > 0 && (
              <StatCard
                label={`${tc('documents:payments.filters.direction_outbound', { defaultValue: 'Paid out' })} · ${cur}`}
                value={<AnimatedNumber value={sums.out} format={moneyFmt(cur)} />}
                icon={Wallet}
                tone="warning"
              />
            )}
          </Fragment>
        ) : (
          <StatCard
            key={`val-${cur}`}
            label={`${tc('summary.value', { defaultValue: 'Value' })} · ${cur}`}
            value={<AnimatedNumber value={sums.total} format={moneyFmt(cur)} />}
            icon={Coins}
            tone="info"
          />
        )
      ))}
      {summary.hasPaid &&
        summary.byCurrency.slice(0, 3).map(([cur, sums]) => (
          <StatCard
            key={`out-${cur}`}
            label={`${tc('summary.outstanding', { defaultValue: 'Outstanding' })} · ${cur}`}
            value={<AnimatedNumber value={Math.max(0, sums.total - sums.paid)} format={moneyFmt(cur)} />}
            icon={Wallet}
            tone={sums.total - sums.paid > 0 ? 'warning' : 'success'}
          />
        ))}
      {/* Past its due date and still owed — the one tile that names work to do.
          Only when there IS something overdue: a zero in red is just noise. */}
      {summary.overdue > 0 && (
        <StatCard
          label={tc('documents:columnsShared.pay_overdue', { defaultValue: 'Overdue' })}
          value={<AnimatedNumber value={summary.overdue} format={intFmt} />}
          icon={Wallet}
          tone="danger"
          hint={tc('summary.documents', { defaultValue: 'documents' })}
        />
      )}
      {summary.byStatus.map(([status, count]) => (
        <StatCard
          key={`st-${status}`}
          label={t(`status.${status}`, { defaultValue: status })}
          value={<AnimatedNumber value={count} format={intFmt} />}
          tone={(statusTone?.[status] ?? 'neutral') as StatTone}
          // Its own money, per currency — the cancelled tile's is NOT in the totals.
          hint={[...(summary.statusAmounts.get(status)?.entries() ?? [])]
            .filter(([, v]) => v !== 0)
            .map(([cur, v]) => formatMoney(v.toFixed(2), { locale: i18n.language, currency: cur }))
            .join(' · ') || undefined}
        />
      ))}
    </div>
  );

  const supplierRefOf = (row: DocRow): string => (row as { supplierInvoiceNo?: string | null }).supplierInvoiceNo ?? '';
  const columns: DataTableColumn<DocRow>[] = [
    {
      id: 'docNumber',
      header: t('columns.docNumber'),
      sortValue: (row) => row.docNumber,
      searchValue: (row) => row.docNumber,
      cellClassName: 'font-mono text-xs',
      cell: (row) => {
        const number = detailPath ? (
          <Link
            to={`${detailPath}/${row.id}`}
            className="font-medium text-fg underline-offset-4 hover:underline hover:text-brand focus:outline-none focus-visible:underline"
          >
            {row.docNumber}
          </Link>
        ) : (
          row.docNumber
        );
        return number;
      },
    },
    // Bills: the supplier's OWN invoice number, its own column — "find invoice
    // 4471" is asked in THEIR numbering, not ours. It used to be a faint second
    // line under our number, easy to miss and impossible to sort by.
    ...(endpoint === '/bills'
      ? [{
        id: 'supplierInvoiceNo',
        header: tc('documents:detail.supplierInvoiceNo', { defaultValue: 'Supplier invoice no.' }),
        sortValue: (row: DocRow) => supplierRefOf(row),
        searchValue: (row: DocRow) => supplierRefOf(row),
        cellClassName: 'font-mono text-xs',
        cell: (row: DocRow) => (supplierRefOf(row)
          ? <span className="font-medium" dir="ltr">{supplierRefOf(row)}</span>
          : <span className="text-fg-muted">—</span>),
      } satisfies DataTableColumn<DocRow>]
      : []),
    {
      id: 'date',
      header: t('columns.date'),
      sortValue: (row) => dateOf(row) ?? '',
      cellClassName: 'text-fg-muted',
      cell: (row) => {
        const date = dateOf(row);
        return date ? formatDate(date, { locale: i18n.language }) : '—';
      },
    },
    {
      id: 'party',
      header: t('columns.party'),
      sortValue: (row) => partyOf(row)?.displayName ?? '',
      searchValue: (row) => {
        const party = partyOf(row);
        return party ? `${party.code} ${party.displayName}` : '';
      },
      cellClassName: 'text-xs',
      cell: (row) => {
        const party = partyOf(row);
        if (!party) return <span className="text-fg-muted">—</span>;
        const body = (
          <>
            <span className="font-mono text-fg-muted">{party.code}</span>{' '}
            <span className="font-medium">{party.displayName}</span>
          </>
        );
        // Straight to the customer's file. Only linked when the row actually
        // carries an id and the reader may open the page — `parties.read`
        // guards both party routes, so without it this is a link to a refusal.
        // The id can be missing on a row the server resolved by name alone.
        return party.id && canReadParties ? (
          <Link
            to={partyPath(endpoint, party.id, row.direction)}
            className="underline-offset-4 hover:underline hover:text-brand focus:outline-none focus-visible:underline"
          >
            {body}
          </Link>
        ) : body;
      },
    },
    ...(showWarehouse
      ? [{
        id: 'warehouse',
        header: tc('documents:detail.warehouse', { defaultValue: 'Warehouse' }),
        sortValue: (row: DocRow) => (row.warehouseId ? warehouseNames.get(row.warehouseId) ?? '' : ''),
        searchValue: (row: DocRow) => (row.warehouseId ? warehouseNames.get(row.warehouseId) ?? '' : ''),
        cellClassName: 'text-xs',
        cell: (row: DocRow) => (row.warehouseId
          ? <span className="font-medium">{warehouseNames.get(row.warehouseId) ?? '—'}</span>
          // No choice recorded: a bill/PO falls to the tenant default; an
          // invoice ships by inference (from where the stock actually is).
          : (
            <span className="text-fg-muted">
              {endpoint === '/invoices' || endpoint === '/sales-orders'
                ? tc('documents:columnsShared.autoWarehouse', { defaultValue: 'Auto' })
                : tc('documents:columnsShared.defaultWarehouse', { defaultValue: 'Default' })}
            </span>
          )),
      } satisfies DataTableColumn<DocRow>]
      : []),
    ...(showSalesRep
      ? [{
        id: 'salesRep',
        header: tc('mandobOrders.rep', { defaultValue: 'Rep' }),
        sortValue: (row: DocRow) => row.salesRepName ?? '',
        searchValue: (row: DocRow) => row.salesRepName ?? '',
        cellClassName: 'text-xs',
        cell: (row: DocRow) => (row.salesRepName
          ? <span className="font-medium">{row.salesRepName}</span>
          : <span className="text-fg-muted">—</span>),
      } satisfies DataTableColumn<DocRow>]
      : []),
    // Invoices: where it was sold — a cashier sale reads «الكاشير» at a glance,
    // the same words the returns list uses. Presence-driven like the money columns.
    ...(showSource
      ? [{
        id: 'source',
        header: tc('documents:columnsShared.source', { defaultValue: 'Sold at' }),
        sortValue: (row: DocRow) => row.source ?? '',
        searchValue: (row: DocRow) => (row.source ? sourceLabel(row.source) : ''),
        cell: (row: DocRow) => (row.source
          ? <Badge variant={SOURCE_TONE[row.source]}>{sourceLabel(row.source)}</Badge>
          : <span className="text-fg-muted">—</span>),
      } satisfies DataTableColumn<DocRow>]
      : []),
    {
      // Off by default via the table's own column picker if a shop does not want
      // it; shown for every document type because 'who wrote this' is asked of
      // all of them.
      id: 'createdBy',
      // The shared label: the per-doctype `columns` blocks have no createdBy, so
      // the prefixed lookup fell through to the English default in every language.
      header: tc('documents:columns.createdBy', { defaultValue: 'Created by' }),
      sortValue: (row) => row.createdByName ?? '',
      searchValue: (row) => row.createdByName ?? '',
      cellClassName: 'text-xs',
      cell: (row) => (row.createdByName
        ? <span className="font-medium">{row.createdByName}</span>
        : <span className="text-fg-muted">—</span>),
    },
    // What a person wrote on the document (the till's own "POS sale …" /
    // "Converted from order …" lines left out), searchable — "the one marked
    // free sample" is found by its note.
    ...(showNotes
      ? [{
        id: 'notes',
        header: tc('documents:form.notes', { defaultValue: 'Notes' }),
        searchValue: (row: DocRow) => userNote((row as { notes?: string | null }).notes),
        cellClassName: 'text-xs max-w-[18rem]',
        cell: (row: DocRow) => {
          const note = userNote((row as { notes?: string | null }).notes);
          return note
            ? <span className="line-clamp-2 whitespace-pre-line" title={note}><bdi>{note}</bdi></span>
            : <span className="text-fg-muted">—</span>;
        },
      } satisfies DataTableColumn<DocRow>]
      : []),
    {
      id: 'status',
      header: t('columns.status'),
      sortValue: (row) => t(`status.${row.status}`, { defaultValue: row.status }),
      searchValue: (row) => t(`status.${row.status}`, { defaultValue: row.status }),
      cell: (row) => {
        const tone = statusTone?.[row.status] ?? 'neutral';
        const variant = TONE_TO_VARIANT[tone] ?? 'secondary';
        return (
          <Badge variant={variant}>
            {t(`status.${row.status}`, { defaultValue: row.status })}
          </Badge>
        );
      },
    },
    // Only for document types that HAVE lines: the server leaves both counts
    // undefined for payments, so this asks the data rather than being told.
    ...((items ?? []).some((r) => r.lineCount !== undefined)
      ? [{
        id: 'items',
        header: tc('documents:columnsShared.items', { defaultValue: 'Items' }),
        align: 'end' as const,
        sortValue: (row: DocRow) => row.lineCount ?? 0,
        searchValue: (row: DocRow) => String(row.lineCount ?? ''),
        cellClassName: 'font-mono tabular-nums text-xs',
        cell: (row: DocRow) => (row.lineCount === undefined
          ? <span className="text-fg-muted">—</span>
          : (
            <span title={tc('documents:columnsShared.itemsHint', {
              defaultValue: '{{lines}} products · {{units}} units',
              lines: row.lineCount,
              units: row.unitCount ?? 0,
            })}>
              {row.lineCount}
              {' '}
              <span className="text-fg-muted">({formatNumber(row.unitCount ?? 0, { locale: i18n.language })})</span>
            </span>
          )),
      } satisfies DataTableColumn<DocRow>]
      : []),
    {
      id: 'amount',
      header: t('columns.amount'),
      align: 'end',
      sortValue: (row) => Number(row.total ?? row.amount ?? '0'),
      cellClassName: 'font-mono tabular-nums',
      cell: (row) => {
        const text = formatMoney(row.total ?? row.amount ?? '0', {
          locale: i18n.language,
          currency: row.currencyCode,
        });
        // Cancelled / voided: still readable, visibly not counted.
        if (isVoidDoc(row)) return <span className="text-fg-subtle line-through" dir="ltr">{text}</span>;
        // Payments only: money in and money out sit in the same list and read
        // identically as bare numbers. Colour + sign is the fastest way to tell
        // a receipt from a supplier payment while scanning the column.
        if (!row.direction) return text;
        const out = row.direction === 'outbound';
        return (
          <span className={out ? 'font-semibold text-warning' : 'font-semibold text-success'} dir="ltr">
            {out ? '−' : '+'}{text}
          </span>
        );
      },
    },
    // Payments only. How the money moved, what it settled, and — the question
    // the counter is actually asked — what the party still owes after it.
    ...(isPayments
      ? [{
        id: 'method',
        header: tc('documents:payments.columns.method', { defaultValue: 'Method' }),
        sortValue: (row: DocRow) => row.method ?? '',
        searchValue: (row: DocRow) => row.method ?? '',
        cellClassName: 'text-xs',
        cell: (row: DocRow) => {
          // An empty method is CASH, not "unknown" — same rule the voucher prints.
          const key = (row.method ?? 'cash').toLowerCase();
          return (
            <span className="text-fg-muted">
              {tc(`documents:payments.voucher.method.${key}`, { defaultValue: row.method ?? 'Cash' })}
            </span>
          );
        },
      } satisfies DataTableColumn<DocRow>]
      : []),
    ...(showSettlement
      ? [
        {
          id: 'settles',
          header: tc('documents:payments.voucher.settles', { defaultValue: 'In settlement of' }),
          sortValue: (row: DocRow) => row.settles?.[0] ?? '',
          searchValue: (row: DocRow) => (row.settles ?? []).join(' '),
          cellClassName: 'text-xs',
          cell: (row: DocRow) => {
            const nums = row.settles ?? [];
            const extra = (row.allocationCount ?? nums.length) - nums.length;
            // Nothing pinned to a document: a deposit against the account. Say
            // so — a dash would read as missing data rather than as a choice.
            if (nums.length === 0) {
              return (
                <span className="text-fg-subtle">
                  {tc('documents:payments.voucher.onAccount', { defaultValue: 'On account' })}
                </span>
              );
            }
            return (
              <span className="font-mono" title={nums.join(' · ')}>
                {nums.join(' · ')}
                {extra > 0 && <span className="text-fg-muted"> +{extra}</span>}
              </span>
            );
          },
        } satisfies DataTableColumn<DocRow>,
        {
          id: 'partyBalance',
          header: tc('documents:columnsShared.remaining', { defaultValue: 'Remaining' }),
          align: 'end' as const,
          sortValue: (row: DocRow) => balanceOf(row),
          searchValue: (row: DocRow) => String(balanceOf(row) || ''),
          cellClassName: 'font-mono tabular-nums text-xs',
          cell: (row: DocRow) => {
            // A walk-in with no party on the payment has no account to be
            // behind on. A green zero here would claim they are square, which
            // is a different statement from "there is nobody to owe anything".
            if (!row.partyId) return <span className="text-fg-muted">—</span>;
            // The server sends only the currencies with something open in them,
            // so an absent row is the answer "nothing left" — shown as a settled
            // zero rather than a dash, because "they're square" is information.
            const left = balanceOf(row);
            const other = (row.partyBalance ?? []).filter((b) => b.currencyCode !== row.currencyCode);
            return (
              <span
                className={left > 0.00005 ? 'font-semibold text-warning' : 'text-success'}
                title={other.length > 0
                  ? other.map((b) => formatMoney(b.amount, { locale: i18n.language, currency: b.currencyCode })).join(' · ')
                  : undefined}
              >
                {formatMoney(left.toFixed(4), { locale: i18n.language, currency: row.currencyCode })}
                {other.length > 0 && <span className="text-fg-muted"> +{other.length}</span>}
              </span>
            );
          },
        } satisfies DataTableColumn<DocRow>,
      ]
      : []),
    // What has been collected, what is still out, and where that leaves the
    // document. Only for the types that carry a paid figure (invoices, bills).
    ...(showPayment
      ? [
        {
          id: 'paid',
          header: tc('documents:columnsShared.paid', { defaultValue: 'Paid' }),
          align: 'end' as const,
          sortValue: (row: DocRow) => numberOf(row.amountPaid),
          cellClassName: 'font-mono tabular-nums text-fg-muted',
          cell: (row: DocRow) => (row.amountPaid == null
            ? <span className="text-fg-muted">—</span>
            : formatMoney(row.amountPaid, { locale: i18n.language, currency: row.currencyCode })),
        } satisfies DataTableColumn<DocRow>,
        {
          id: 'remaining',
          header: tc('documents:columnsShared.remaining', { defaultValue: 'Remaining' }),
          align: 'end' as const,
          sortValue: (row: DocRow) => remainingOf(row),
          // Searchable so "100000" finds the invoice with that balance.
          searchValue: (row: DocRow) => (row.amountPaid == null ? '' : String(remainingOf(row))),
          cellClassName: 'font-mono tabular-nums',
          cell: (row: DocRow) => {
            // Cancelled: nothing is owed on it — a dash, not its old total.
            if (row.amountPaid == null || isVoidDoc(row)) return <span className="text-fg-muted">—</span>;
            const left = remainingOf(row);
            // Nothing left to chase reads as a quiet zero; a balance is the
            // number the shop is actually looking for, so it carries weight.
            return (
              <span className={left > 0 ? 'font-semibold text-fg' : 'text-fg-muted'}>
                {formatMoney(left.toFixed(4), { locale: i18n.language, currency: row.currencyCode })}
              </span>
            );
          },
        } satisfies DataTableColumn<DocRow>,
        {
          id: 'payState',
          header: tc('documents:columnsShared.payState', { defaultValue: 'Payment' }),
          sortValue: (row: DocRow) => payStateOf(row, today),
          searchValue: (row: DocRow) => {
            const s = payStateOf(row, today);
            return s === 'none' ? '' : tc(`documents:columnsShared.pay_${s}`, { defaultValue: s });
          },
          cell: (row: DocRow) => {
            const s = payStateOf(row, today);
            if (s === 'none') return <span className="text-fg-muted">—</span>;
            const due = (row.dueDate ?? '').slice(0, 10);
            return (
              <Badge
                variant={PAY_STATE_TONE[s]}
                title={due
                  ? tc('documents:columnsShared.dueOn', {
                    defaultValue: 'Due {{date}}',
                    date: formatDate(due, { locale: i18n.language }),
                  })
                  : undefined}
              >
                {tc(`documents:columnsShared.pay_${s}`, { defaultValue: s })}
              </Badge>
            );
          },
        } satisfies DataTableColumn<DocRow>,
      ]
      : []),
    ...(printPath || (postPermission && canPost) || canWrite
      ? [
          {
            id: 'actions',
            header: '',
            align: 'end' as const,
            headClassName: 'w-40',
            cell: (row: DocRow) => {
              const isPosted = !!row.postedJournalEntryId;
              const canShowPost =
                !!postPermission &&
                canPost &&
                !isPosted &&
                (row.status === 'draft' ||
                  row.status === 'issued' ||
                  row.status === 'approved');
              // Drafts — and any sales order not already cancelled, for someone
              // allowed to unwind it (matches the order page and the server).
              const canShowRemove = canWrite && (
                row.status === 'draft'
                || (endpoint === '/sales-orders' && canUnwindOrders && row.status !== 'cancelled')
              );
              return (
                <div className="flex justify-end gap-1">
                  {printPath && (
                    // window.open (not an <a target="_blank">): anchors get
                    // implicit noopener, so the new tab wouldn't inherit the
                    // sessionStorage auth and would bounce to login. window.open
                    // keeps the opener, so the print tab stays signed in.
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`${tc('actions.print', { defaultValue: 'Print' })} ${row.docNumber}`}
                      title={tc('actions.print', { defaultValue: 'Print' })}
                      onClick={() => window.open(appUrl(`${printPath}/${row.id}/print`), '_blank')}
                    >
                      <Printer className="h-4 w-4" aria-hidden />
                    </Button>
                  )}
                  {canShowPost && (
                    <Button variant="ghost" size="sm" onClick={() => onPost(row.id)}>
                      {tc('actions.post', { defaultValue: 'Post' })}
                    </Button>
                  )}
                  {editable && detailPath && canWrite && row.status === 'draft' && (
                    <Link to={`${detailPath}/${row.id}/edit`}>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`${tc('actions.edit', { defaultValue: 'Edit' })} ${row.docNumber}`}
                        title={tc('actions.edit', { defaultValue: 'Edit' })}
                      >
                        <Pencil className="h-4 w-4" aria-hidden />
                      </Button>
                    </Link>
                  )}
                  {canShowRemove && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemove(row)}
                      aria-label={`${tc('actions.remove', { defaultValue: 'Remove' })} ${row.docNumber}`}
                      title={tc('actions.remove', { defaultValue: 'Remove' })}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  )}
                </div>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      {header}
      {summaryStrip}
      <div className="flex flex-wrap items-end gap-2">
        {showBilledFilter && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-fg-muted">
              {tc('documents:columnsShared.billedFilter', { defaultValue: 'Show' })}
            </span>
            <div className="flex h-9 items-center gap-1 rounded-md border border-border bg-bg p-0.5">
              {(['open', 'invoiced', 'all'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setBilled(k)}
                  className={'rounded px-2.5 py-1 text-xs font-medium transition '
                    + (billed === k ? 'bg-brand text-brand-contrast' : 'text-fg-muted hover:text-fg')}
                >
                  {tc(`documents:columnsShared.billed_${k}`, {
                    defaultValue: k === 'open' ? 'Not invoiced' : k === 'invoiced' ? 'Invoiced' : 'All',
                  })}
                </button>
              ))}
            </div>
          </div>
        )}
        {isPayments && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-fg-muted">
              {tc('documents:payments.filters.direction', { defaultValue: 'Direction' })}
            </span>
            <div className="flex h-9 items-center gap-1 rounded-md border border-border bg-bg p-0.5">
              {(['all', 'inbound', 'outbound'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setDirectionFilter(k)}
                  className={'rounded px-2.5 py-1 text-xs font-medium transition '
                    + (directionFilter === k ? 'bg-brand text-brand-contrast' : 'text-fg-muted hover:text-fg')}
                >
                  {tc(`documents:payments.filters.direction_${k}`, {
                    defaultValue: k === 'inbound' ? 'Received' : k === 'outbound' ? 'Paid out' : 'All',
                  })}
                </button>
              ))}
            </div>
          </div>
        )}
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-fg-muted">{tc('filters.from', { defaultValue: 'From' })}</span>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-fg-muted">{tc('filters.to', { defaultValue: 'To' })}</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-fg-muted">{t('columns.status')}</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand">
            <option value="all">{tc('filters.allStatuses', { defaultValue: 'All statuses' })}</option>
            {statuses.map((s) => (
              <option key={s} value={s}>{t(`status.${s}`, { defaultValue: s })}</option>
            ))}
          </select>
        </label>
        {showPayment && (
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-fg-muted">
              {tc('documents:columnsShared.payState', { defaultValue: 'Payment' })}
            </span>
            <select
              value={payFilter}
              onChange={(e) => setPayFilter(e.target.value as 'all' | Exclude<PayState, 'none'>)}
              className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand"
            >
              <option value="all">{tc('documents:columnsShared.allPayStates', { defaultValue: 'All payments' })}</option>
              {(['overdue', 'unpaid', 'partial', 'paid'] as const).map((s) => (
                <option key={s} value={s}>{tc(`documents:columnsShared.pay_${s}`, { defaultValue: s })}</option>
              ))}
            </select>
          </label>
        )}
        {showSource && (
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-fg-muted">{tc('documents:columnsShared.source', { defaultValue: 'Sold at' })}</span>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value as 'all' | SaleSource)}
              className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand"
            >
              <option value="all">{tc('documents:columnsShared.allSources', { defaultValue: 'Everywhere' })}</option>
              {SALE_SOURCES.filter((s) => items.some((r) => r.source === s)).map((s) => (
                <option key={s} value={s}>{sourceLabel(s)}</option>
              ))}
            </select>
          </label>
        )}
        {showWarehouse && warehouseNames.size > 1 && (
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-fg-muted">{tc('filters.branch', { defaultValue: 'Branch' })}</span>
            <select value={warehouseFilter} onChange={(e) => setWarehouseFilter(e.target.value)} className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand">
              <option value="all">{tc('filters.allBranches', { defaultValue: 'All branches' })}</option>
              {[...warehouseNames.entries()].map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
              <option value="none">{tc('filters.noBranch', { defaultValue: 'Unbranched' })}</option>
            </select>
          </label>
        )}
        <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-border bg-bg px-2.5 text-sm text-fg">
          <input
            type="checkbox"
            checked={showNotes}
            onChange={(e) => setShowNotes(e.target.checked)}
            className="h-4 w-4 accent-[var(--color-brand)]"
          />
          {tc('documents:columnsShared.showNotes', { defaultValue: 'Show notes' })}
        </label>
        {anyFilter && (
          <Button variant="ghost" size="sm" onClick={() => { setFromDate(''); setToDate(''); setStatusFilter('all'); setWarehouseFilter('all'); setPayFilter('all'); setDirectionFilter('all'); setSourceFilter('all'); }}>
            {tc('filters.clear', { defaultValue: 'Clear' })}
          </Button>
        )}
      </div>
      {/* How much of the list is here — still arriving, or capped with the way to the rest. */}
      {loadState.loading ? (
        <p className="text-xs text-fg-muted">
          {tc('documents:columnsShared.loadingRows', {
            have: formatNumber(loadState.have, { locale: i18n.language }), total: formatNumber(loadState.total, { locale: i18n.language }),
            defaultValue: 'Loading {{have}} of {{total}}…',
          })}
        </p>
      ) : loadState.total > items.length ? (
        <p className="text-xs text-warning">
          {tc('documents:columnsShared.rowsCapped', {
            have: formatNumber(items.length, { locale: i18n.language }), total: formatNumber(loadState.total, { locale: i18n.language }),
            defaultValue: 'Showing the newest {{have}} of {{total}} — pick a date range to see older ones.',
          })}
        </p>
      ) : null}
      <Card className="overflow-hidden p-0">
        <DataTable
          columns={columns}
          data={filteredItems}
          rowKey={(row) => row.id}
          selectable={canBulk}
          bulkActions={(rows, clear) => {
            const postable = rows.filter(isPostable).length;
            const deletable = rows.filter(isDeletable).length;
            return (
              <>
                {postPermission && canPost && postable > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => void bulkPost(rows, clear)}>
                    {tc('actions.post', { defaultValue: 'Post' })} ({postable})
                  </Button>
                )}
                {canWrite && deletable > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => void bulkDelete(rows, clear)}>
                    {tc('actions.delete', { defaultValue: 'Delete' })} ({deletable})
                  </Button>
                )}
              </>
            );
          }}
        />
      </Card>
    </div>
  );
}
